import { useEffect, useState } from 'react';
import type { HeyGenDimension } from '../../../types/avatar-video';
import { getAvatarsOrientation, type AvatarsOrientation } from '../utils/avatars-orientation';
import { resolveVideoDuration } from '../utils/resolve-video-duration';
import { getAvatarsPlacas } from '../../../clients/avatars-placas-registry';

export type ResolvedOutroPlaca = {
  videoUrl: string;
  durationMs: number;
};

type UseAvatarsOutroPlacaOptions = {
  clientId: string;
  dimension: HeyGenDimension | null;
  placaId: string | null;
  hasSavedPiece: boolean;
};

type UseAvatarsOutroPlacaResult = {
  outro: ResolvedOutroPlaca | null;
  isReady: boolean;
};

const tryGetOrientation = (dimension: HeyGenDimension): AvatarsOrientation | null => {
  try {
    return getAvatarsOrientation(dimension);
  } catch (err) {
    console.warn('[avatars-placas] dimensión no soportada por orientación', err);
    return null;
  }
};

/**
 * Resuelve la placa de cierre para el primer boot del editor de Avatars.
 *
 * Flujo:
 *   1. Si no hay dimension todavía → idle. Para proyectos legacy.
 *   2. Si hay `editorState` → no inyectamos (isReady=true, outro=null).
 *   3. Si `placaId` es null → user eligió "Sin placa" → no inyectamos.
 *   4. Si el cliente no tiene config → no inyectamos.
 *   5. Probe de duración con `resolveVideoDuration` → outro listo.
 *
 * Cualquier fallo intermedio termina en `isReady=true, outro=null` (silent skip)
 * para no bloquear el boot del editor.
 */
export const useAvatarsOutroPlaca = ({
  clientId,
  dimension,
  placaId,
  hasSavedPiece
}: UseAvatarsOutroPlacaOptions): UseAvatarsOutroPlacaResult => {
  const [outro, setOutro] = useState<ResolvedOutroPlaca | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!dimension) return;

    setOutro(null);
    setIsReady(false);

    if (hasSavedPiece) {
      setIsReady(true);
      return;
    }

    if (!placaId) {
      setIsReady(true);
      return;
    }

    const placas = getAvatarsPlacas(clientId);
    if (!placas) {
      setIsReady(true);
      return;
    }

    const orientation = tryGetOrientation(dimension);
    if (!orientation) {
      setIsReady(true);
      return;
    }

    const placa = placas.outros?.find(p => p.id === placaId);
    if (!placa) {
      console.warn(`[avatars-placas] placaId="${placaId}" no existe para el cliente ${clientId}, skip.`);
      setIsReady(true);
      return;
    }

    const file = placa.files[orientation];
    if (!file) {
      console.warn(`[avatars-placas] placa "${placaId}" no tiene archivo ${orientation}, skip.`);
      setIsReady(true);
      return;
    }

    let cancelled = false;
    resolveVideoDuration(file.videoUrl)
      .then(durationMs => {
        if (!cancelled) setOutro({ videoUrl: file.videoUrl, durationMs });
      })
      .catch(err => {
        if (!cancelled) console.warn('[avatars-placas] probe de duración falló', err);
      })
      .finally(() => {
        if (!cancelled) setIsReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, dimension, placaId, hasSavedPiece]);

  return { outro, isReady };
};
